import React, { useState, useEffect } from 'react';
import ProfileList from '../ProfileList';
import ProfileEditor from '../ProfileEditor';
import csvImportApiClient from '../../utilities/csvImportApiClient';
import Styles from './styles.module.css';

const CSVImportSettings = () => {
    const [profiles, setProfiles] = useState([]);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [salesChannels, setSalesChannels] = useState([]);
    const [shippingMethods, setShippingMethods] = useState([]);
    const [dataLoadError, setDataLoadError] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            setDataLoadError(null);
            try {
                await Promise.all([
                    loadProfiles(isMounted),
                    loadSalesChannels(isMounted),
                    loadShippingMethods(isMounted)
                ]);
            } catch (error) {
                if (isMounted) {
                    setDataLoadError('Fehler beim Laden der Daten. Token möglicherweise abgelaufen.');
                    console.error('Data loading failed:', error);
                }
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, []);

    const loadProfiles = async (isMounted = true) => {
        if (!isMounted) return;
        setIsLoading(true);
        console.log('[CSVImportSettings] Loading profiles...');
        try {
            const response = await csvImportApiClient.getImportProfiles();
            console.log('[CSVImportSettings] Profile response:', response);
            if (!isMounted) return; // Check again after async call
            
            // Sort profiles alphabetically by name (A-Z)
            const sortedProfiles = (response.data || []).sort((a, b) => {
                const nameA = (a.name || '').toLowerCase();
                const nameB = (b.name || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
            setProfiles(sortedProfiles);
        } catch (error) {
            console.error('Error loading profiles:', error);
            if (isMounted) setProfiles([]);
        } finally {
            if (isMounted) setIsLoading(false);
        }
    };

    const loadSalesChannels = async (isMounted = true) => {
        if (!isMounted) return;
        try {
            console.log('[CSVImportSettings] Loading sales channels...');
            const response = await csvImportApiClient.getSalesChannels();
            if (!isMounted) return; // Check again after async call
            
            if (response.success) {
                console.log('[CSVImportSettings] Sales channels loaded:', response.data?.length || 0);
                setSalesChannels(response.data || []);
            } else {
                console.warn('[CSVImportSettings] Failed to load sales channels:', response.error);
                setSalesChannels([]);
            }
        } catch (error) {
            console.error('Error loading sales channels:', error);
            if (isMounted) setSalesChannels([]);
        }
    };

    const loadShippingMethods = async (isMounted = true) => {
        if (!isMounted) return;
        try {
            console.log('[CSVImportSettings] Loading shipping methods...');
            const response = await csvImportApiClient.getShippingMethods();
            if (!isMounted) return; // Check again after async call
            
            if (response.success) {
                console.log('[CSVImportSettings] Shipping methods loaded:', response.data?.length || 0);
                setShippingMethods(response.data || []);
            } else {
                console.warn('[CSVImportSettings] Failed to load shipping methods:', response.error);
                setShippingMethods([]);
            }
        } catch (error) {
            console.error('Error loading shipping methods:', error);
            if (isMounted) setShippingMethods([]);
        }
    };

    const handleCreateProfile = () => {
        setSelectedProfile(null);
        setIsCreating(true);
        setIsEditing(true);
    };

    const handleEditProfile = (profile) => {
        setSelectedProfile(profile);
        setIsCreating(false);
        setIsEditing(true);
    };

    const handleDeleteProfile = async (profileId) => {
        console.log('[CSVImportSettings] Delete requested for profile:', profileId);
        if (window.confirm('Sind Sie sicher, dass Sie dieses Profil löschen möchten?')) {
            try {
                console.log('[CSVImportSettings] Starting deletion of profile:', profileId);
                const result = await csvImportApiClient.deleteImportProfile(profileId);
                console.log('[CSVImportSettings] Delete result:', result);
                
                if (result && result.success) {
                    console.log('[Profile Delete] Success, reloading profiles...');
                    await loadProfiles();
                    if (selectedProfile?.id === profileId) {
                        setSelectedProfile(null);
                        setIsEditing(false);
                    }
                    
                    // Show appropriate success message based on the deletion result
                    if (result.warning) {
                        alert(`Profil gelöscht!\n\nHinweis: ${result.warning}`);
                    } else {
                        alert('Profil erfolgreich gelöscht!');
                    }
                } else {
                    throw new Error(result?.message || 'Unbekannter Fehler beim Löschen');
                }
            } catch (error) {
                console.error('Error deleting profile:', error);
                alert('Fehler beim Löschen des Profils: ' + error.message);
            }
        }
    };

    const handleCopyProfile = async (profile) => {
        try {
            const result = await csvImportApiClient.copyImportProfile(profile.id);
            if (result.success) {
                await loadProfiles();
                // Reload sales channels and shipping methods to ensure they're available
                await loadSalesChannels();
                await loadShippingMethods();
                // Show success message
                if (result.warning) {
                    alert(`Profil erfolgreich kopiert!\n\nHinweis: ${result.warning}`);
                } else {
                    alert('Profil erfolgreich kopiert!');
                }
            } else {
                throw new Error(result.message || 'Unbekannter Fehler');
            }
        } catch (error) {
            console.error('Error copying profile:', error);
            alert('Fehler beim Kopieren des Profils: ' + error.message);
        }
    };

    const handleExportProfiles = () => {
        try {
            const exportData = {
                version: '1.0',
                exportDate: new Date().toISOString(),
                profiles: profiles
            };
            
            const dataStr = JSON.stringify(exportData, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `csv-import-profiles-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            alert('Profile erfolgreich exportiert!');
        } catch (error) {
            console.error('Error exporting profiles:', error);
            alert('Fehler beim Exportieren der Profile: ' + error.message);
        }
    };

    const handleImportProfiles = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.name.endsWith('.json')) {
            alert('Bitte wählen Sie eine JSON-Datei aus.');
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const importData = JSON.parse(e.target.result);
                
                if (!importData.profiles || !Array.isArray(importData.profiles)) {
                    throw new Error('Ungültiges Dateiformat. Die Datei muss ein "profiles" Array enthalten.');
                }

                const confirmMessage = `Möchten Sie ${importData.profiles.length} Profile importieren?\n\nImport-Datum: ${new Date(importData.exportDate || 'Unbekannt').toLocaleDateString('de-DE')}\n\nVorhandene Profile werden nicht überschrieben.`;
                
                if (!window.confirm(confirmMessage)) {
                    return;
                }

                let successCount = 0;
                let errorCount = 0;
                const errors = [];

                for (const profile of importData.profiles) {
                    try {
                        // Generate new ID to avoid conflicts
                        const profileData = {
                            ...profile,
                            name: `${profile.name} (Importiert)`,
                            description: profile.description ? `${profile.description} (Importiert am ${new Date().toLocaleDateString('de-DE')})` : `Importiert am ${new Date().toLocaleDateString('de-DE')}`
                        };
                        
                        // Remove ID fields so new ones are generated
                        delete profileData.id;
                        delete profileData.createdAt;
                        delete profileData.updatedAt;

                        const result = await csvImportApiClient.createImportProfile(profileData);
                        if (result.success) {
                            successCount++;
                        } else {
                            errorCount++;
                            errors.push(`${profile.name}: ${result.message || 'Unbekannter Fehler'}`);
                        }
                    } catch (error) {
                        errorCount++;
                        errors.push(`${profile.name}: ${error.message}`);
                    }
                }

                await loadProfiles();

                let message = `Import abgeschlossen!\n\nErfolgreich: ${successCount}\nFehler: ${errorCount}`;
                if (errors.length > 0) {
                    message += '\n\nFehler-Details:\n' + errors.slice(0, 5).join('\n');
                    if (errors.length > 5) {
                        message += `\n... und ${errors.length - 5} weitere Fehler`;
                    }
                }
                alert(message);
                
            } catch (error) {
                console.error('Error importing profiles:', error);
                alert('Fehler beim Importieren der Profile: ' + error.message);
            }
        };
        
        reader.onerror = () => {
            alert('Fehler beim Lesen der Datei.');
        };
        
        reader.readAsText(file);
        
        // Reset file input
        event.target.value = '';
    };

    const handleSaveProfile = async (profileData) => {
        console.log('[CSVImportSettings] Saving profile:', { isCreating, profileId: selectedProfile?.id, profileData });
        try {
            let result;
            if (isCreating) {
                console.log('[CSVImportSettings] Creating new profile...');
                result = await csvImportApiClient.createImportProfile(profileData);
            } else {
                console.log('[CSVImportSettings] Updating profile:', selectedProfile.id);
                result = await csvImportApiClient.updateImportProfile(selectedProfile.id, profileData);
            }
            console.log('[CSVImportSettings] Save result:', result);
            
            await loadProfiles();
            
            // Update selectedProfile with fresh data instead of clearing it
            if (!isCreating && result && result.success) {
                const updatedProfile = {
                    ...selectedProfile,
                    ...profileData,
                    updatedAt: new Date().toISOString()
                };
                setSelectedProfile(updatedProfile);
            } else {
                setSelectedProfile(null);
            }
            
            setIsEditing(false);
            setIsCreating(false);
        } catch (error) {
            console.error('Error saving profile:', error);
            alert('Fehler beim Speichern des Profils: ' + error.message);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setIsCreating(false);
        setSelectedProfile(null);
    };

    const getImportTypeDisplay = (config) => {
        if (!config) return 'Standard';
        
        switch (config.importType) {
            case 'single_customer_multiple_orders':
                return 'Ein Kunde, mehrere Bestellungen';
            case 'single_customer_single_order':
            case 'single_customer_single_order_with_prices':
            case 'single_customer_single_order_without_prices':
                return 'Ein Kunde, eine Bestellung';
            case 'multiple_customers_multiple_orders':
                return 'Mehrere Kunden, mehrere Bestellungen';
            default:
                return 'Standard';
        }
    };

    return (
        <div className={Styles.container}>
            <div className={Styles.header}>
                <h1>Import-Profile verwalten</h1>
                <p>Erstellen und bearbeiten Sie Profile für verschiedene CSV-Import-Workflows</p>
            </div>

            <div className={Styles.content}>
                {!isEditing ? (
                    <div className={Styles.profileManagement}>
                        <div className={Styles.toolbar}>
                            {dataLoadError && (
                                <div className={Styles.errorMessage}>
                                    ⚠️ {dataLoadError}
                                    <button
                                        className={Styles.refreshButton}
                                        onClick={() => window.location.reload()}
                                        title="Seite neu laden"
                                    >
                                        🔄 Neu laden
                                    </button>
                                </div>
                            )}
                            <button
                                className={Styles.createButton}
                                onClick={handleCreateProfile}
                            >
                                + Neues Profil erstellen
                            </button>
                        </div>

                        <ProfileList
                            profiles={profiles}
                            isLoading={isLoading}
                            onEdit={handleEditProfile}
                            onDelete={handleDeleteProfile}
                            onCopy={handleCopyProfile}
                            getImportTypeDisplay={getImportTypeDisplay}
                            salesChannels={salesChannels}
                            shippingMethods={shippingMethods}
                        />

                        {profiles.length === 0 && !isLoading && (
                            <div className={Styles.emptyState}>
                                <h3>Keine Profile vorhanden</h3>
                                <p>Erstellen Sie Ihr erstes Import-Profil, um CSV-Dateien importieren zu können.</p>
                                <button
                                    className={Styles.createButton}
                                    onClick={handleCreateProfile}
                                >
                                    Erstes Profil erstellen
                                </button>
                            </div>
                        )}

                        {/* Export/Import Section */}
                        <div className={Styles.exportImportSection}>
                            <div className={Styles.exportImportHeader}>
                                <h4>Profile sichern & wiederherstellen</h4>
                                <p>Exportieren Sie Ihre Profile als JSON-Datei oder importieren Sie Profile aus einer Backup-Datei.</p>
                            </div>
                            
                            <div className={Styles.exportImportActions}>
                                <button
                                    className={Styles.exportButton}
                                    onClick={handleExportProfiles}
                                    disabled={profiles.length === 0}
                                    title={profiles.length === 0 ? 'Keine Profile zum Exportieren vorhanden' : 'Alle Profile als JSON exportieren'}
                                >
                                    📥 Profile exportieren ({profiles.length})
                                </button>
                                
                                <div className={Styles.importContainer}>
                                    <input
                                        type="file"
                                        accept=".json"
                                        onChange={handleImportProfiles}
                                        className={Styles.importInput}
                                        id="profileImportInput"
                                    />
                                    <label 
                                        htmlFor="profileImportInput" 
                                        className={Styles.importButton}
                                        title="Profile aus JSON-Datei importieren"
                                    >
                                        📤 Profile importieren
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <ProfileEditor
                        profile={selectedProfile}
                        isCreating={isCreating}
                        onSave={handleSaveProfile}
                        onCancel={handleCancelEdit}
                    />
                )}
            </div>
        </div>
    );
};

export default CSVImportSettings;